/**
 * Turning whatever a promise rejected with into something a Slack alert can
 * act on. `String(reason)` gives "[object Object]" for anything that isn't an
 * Error, which is how a burst of fourteen identical, useless alerts reached
 * Slack from one page view.
 */

/** A readable one-line description of a rejection reason. */
export function describeRejection(reason: unknown): string {
  if (reason instanceof Error) return reason.message || reason.name;
  if (reason == null) return "Unhandled promise rejection (no reason given)";
  if (typeof reason !== "object") return String(reason);

  // A DOM event: a failed image, script or media load, usually.
  if (typeof Event !== "undefined" && reason instanceof Event) {
    const el = reason.target as (Element & { src?: string; href?: string }) | null;
    const where = el && "tagName" in el ? ` on <${el.tagName.toLowerCase()}>${el.src || el.href ? ` ${el.src || el.href}` : ""}` : "";
    return `Rejected with a "${reason.type}" event${where}`;
  }

  const obj = reason as Record<string, unknown>;
  const kind = obj.constructor && obj.constructor !== Object ? (obj.constructor as { name?: string }).name : "object";
  const message = typeof obj.message === "string" ? obj.message : typeof obj.error === "string" ? obj.error : "";
  if (message) return `${kind}: ${message}`;
  let json = "";
  try { json = JSON.stringify(reason); } catch { /* circular */ }
  return json && json !== "{}" ? `Rejected with ${kind} ${json.slice(0, 200)}` : `Rejected with ${kind} (no message)`;
}

/** A message that says nothing; old bundles still send it. */
export function isUninformative(message: string): boolean {
  return /^\s*\[object \w+\]\s*$/.test(message);
}

const sent = new Set<string>();

/** True the first time a message is seen on this page, false after. */
export function firstTimeOnPage(message: string): boolean {
  if (sent.has(message)) return false;
  sent.add(message);
  return true;
}
